import React, { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/Tabs";
import { OperationSaleListPage } from "./OperationSaleListPage";
import { OperationPreparationKanbanPage } from "./OperationPreparationKanbanPage";
import { OperationDeliveriesPage } from "./OperationDeliveriesPage";
import { OperationPostReceiptPage } from "./OperationPostReceiptPage";
import { OPERATION_TAB_NAV_KEYS } from "../../config/navigationCatalog";
import { useAccessControl } from "../../contexts/AccessControlContext";

const OPERATION_TABS = [
  { value: "post-sale", label: "Pós-Venda" },
  { value: "preparation", label: "Preparação" },
  { value: "deliveries", label: "Entregas" },
  { value: "receipt", label: "Recebimento" },
] as const;

type OperationTabValue = (typeof OPERATION_TABS)[number]["value"];

export const OperationsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { canAccess, isReady } = useAccessControl();

  const allowedTabs = useMemo(
    () =>
      OPERATION_TABS.filter((tab) => {
        const navKey = OPERATION_TAB_NAV_KEYS[tab.value];
        return navKey ? canAccess(navKey) : false;
      }),
    [canAccess],
  );

  const requestedTab = (searchParams.get("tab") ?? "post-sale") as OperationTabValue;
  const activeTab = allowedTabs.some((tab) => tab.value === requestedTab)
    ? requestedTab
    : allowedTabs[0]?.value;

  useEffect(() => {
    if (!isReady || !activeTab) {
      return;
    }
    if (searchParams.get("tab") !== activeTab) {
      setSearchParams({ tab: activeTab }, { replace: true });
    }
  }, [isReady, activeTab, searchParams, setSearchParams]);

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  if (!isReady) {
    return (
      <div className="min-h-[200px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent" />
      </div>
    );
  }

  if (!activeTab || allowedTabs.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Operações</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Gerencie o fluxo pós-venda, preparação, entregas e pós-recebimento
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList
          className={`grid w-full max-w-3xl`}
          style={{ gridTemplateColumns: `repeat(${allowedTabs.length}, minmax(0, 1fr))` }}
        >
          {allowedTabs.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {allowedTabs.some((tab) => tab.value === "post-sale") && (
          <TabsContent value="post-sale" className="mt-6">
            <OperationSaleListPage
              operationalStage="pre_sale"
              titleKey="operation.postSale.title"
              subtitleKey="operation.postSale.subtitle"
              progressStageKey="pos_venda"
              showAdvance={false}
              getSaleDetailPath={(saleId) => `/operation/preparation/${saleId}`}
            />
          </TabsContent>
        )}

        {allowedTabs.some((tab) => tab.value === "preparation") && (
          <TabsContent value="preparation" className="mt-6">
            <OperationPreparationKanbanPage />
          </TabsContent>
        )}

        {allowedTabs.some((tab) => tab.value === "deliveries") && (
          <TabsContent value="deliveries" className="mt-6">
            <OperationDeliveriesPage />
          </TabsContent>
        )}

        {allowedTabs.some((tab) => tab.value === "receipt") && (
          <TabsContent value="receipt" className="mt-6">
            <OperationPostReceiptPage />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
